import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BookRecommendation } from './useBookRecommendations';

interface CachedClassicRecommendation {
  id: string;
  user_id: string;
  book_id: string | null;
  book_title: string;
  book_author: string | null;
  cover_url: string | null;
  reason: string | null;
  recommendation_type: string | null;
  confidence_score: number | null;
  match_details: string | null;
  source_books: any;
  status: string;
  created_at: string;
  expires_at: string;
}

const CACHE_KEY = 'cachedClassicRecommendation';
const CACHE_DURATION_MS = 1000 * 60 * 60 * 24; // 24 hours

export const useCachedClassicRecommendation = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [CACHE_KEY],
    queryFn: async (): Promise<BookRecommendation | null> => {
      console.log('📚 Checking for cached classic recommendation...');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Try to fetch from cache first
      const { data: cachedRec, error: cacheError } = await supabase
        .from('user_cached_recommendations')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('recommendation_type', 'classic')
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!cacheError && cachedRec) {
        console.log(`✅ Found cached classic recommendation: ${cachedRec.book_title}`);
        return {
          title: cachedRec.book_title,
          author: cachedRec.book_author || 'Unknown',
          reason: cachedRec.reason || '',
          bookId: cachedRec.book_id || undefined,
          coverUrl: cachedRec.cover_url || undefined,
          confidenceScore: cachedRec.confidence_score || undefined,
          recommendationType: 'classic',
          sourceBooks: (Array.isArray(cachedRec.source_books) ? cachedRec.source_books : []) as { id: string; title: string; reason: string; }[],
          matchDetails: cachedRec.match_details || undefined,
        };
      }

      // No cached classic - try to get one from the main recommendations cache
      const { data: anyClassic } = await supabase
        .from('user_cached_recommendations')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('recommendation_type', 'classic')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (anyClassic) {
        console.log(`✅ Found classic from cache: ${anyClassic.book_title}`);
        return {
          title: anyClassic.book_title,
          author: anyClassic.book_author || 'Unknown',
          reason: anyClassic.reason || '',
          bookId: anyClassic.book_id || undefined,
          coverUrl: anyClassic.cover_url || undefined,
          confidenceScore: anyClassic.confidence_score || undefined,
          recommendationType: 'classic',
          sourceBooks: (Array.isArray(anyClassic.source_books) ? anyClassic.source_books : []) as { id: string; title: string; reason: string; }[],
          matchDetails: anyClassic.match_details || undefined,
        };
      }

      console.log('📚 No cached classic found');
      return null;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: (prev: BookRecommendation | null | undefined) => prev ?? undefined,
  });

  const refresh = async () => {
    queryClient.invalidateQueries({ queryKey: [CACHE_KEY] });
  };

  return {
    ...query,
    refresh,
  };
};
